import { t, type MessageKey } from '../../i18n';

export interface Period {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  isBreak: boolean;
}
export interface Lesson {
  id: string;
  sectionId: string;
  sectionName: string;
  className: string;
  periodId: string;
  dayOfWeek: number;
  subjectName: string;
  staffName: string;
  room: string | null;
}

/** Monday first; Saturday and Sunday appear only when something is scheduled on them. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];

/** Weekly grid: periods down the side, days across. */
export function TimetableGrid({
  periods,
  lessons,
  show,
  onRemove,
}: {
  periods: Period[];
  lessons: Lesson[];
  /** Which detail goes under the subject: the teacher (section view) or the section (teacher view). */
  show: 'teacher' | 'section';
  onRemove?: (lesson: Lesson) => void;
}) {
  if (periods.length === 0) return <p className="text-slate-600">{t('tt.noPeriods')}</p>;
  const days = WEEK.filter((d) => (d >= 1 && d <= 5) || lessons.some((l) => l.dayOfWeek === d));
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
        <caption className="sr-only">{t('tt.title')}</caption>
        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
          <tr>
            <th scope="col" className="p-2">
              {t('tt.period')}
            </th>
            {days.map((d) => (
              <th key={d} scope="col" className="p-2">
                {t(`tt.day.${d}` as MessageKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {periods.map((p) => (
            <tr key={p.id} className={p.isBreak ? 'bg-slate-50 text-slate-500' : ''}>
              <th scope="row" className="whitespace-nowrap p-2 font-medium">
                {p.name}
                <span className="block text-xs font-normal text-slate-600">
                  {p.startTime}–{p.endTime}
                </span>
              </th>
              {days.map((d) => {
                const here = lessons.filter((l) => l.periodId === p.id && l.dayOfWeek === d);
                return (
                  <td key={d} className="p-2 align-top">
                    {here.map((l) => (
                      <div key={l.id} className="mb-1">
                        <span className="font-medium">{l.subjectName}</span>
                        <span className="block text-xs text-slate-600">
                          {show === 'teacher' ? l.staffName : `${l.className} – ${l.sectionName}`}
                          {l.room ? ` · ${l.room}` : ''}
                        </span>
                        {onRemove && (
                          <button
                            type="button"
                            className="text-xs text-red-700 underline"
                            aria-label={`${t('tt.removeLesson')}: ${l.subjectName} ${t(`tt.day.${d}` as MessageKey)} ${p.name}`}
                            onClick={() => onRemove(l)}
                          >
                            {t('tt.removeLesson')}
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
